import type { MotionPropsFor, SystemProps } from '@hover/blueprint';
import type { TargetAndTransition } from 'framer-motion';

import { useHomeownerTranslate } from 'src/modules/homeownerApp/hooks';
import { useAiGeneratedImageQuery } from 'src/shared/graphql/generated';

import type { CustomizationHistoryEntry } from '../useCustomization';
import { useCustomization } from '../useCustomization';

export const applyActiveStyles = ({
  isActive,
  isVisible,
  showLoadingOverlay,
  animate: animateProp,
}: {
  isActive: boolean;
  isVisible: boolean;
  showLoadingOverlay?: boolean;
  animate?: TargetAndTransition;
}) =>
  ({
    ...(isVisible ? {} : { 'aria-hidden': 'true' as const }),
    pointerEvents: isVisible ? undefined : 'none',
    animate: {
      ...animateProp,
      opacity: isVisible ? 1 : 0,
      filter:
        isActive && !showLoadingOverlay
          ? 'brightness(1) saturate(1)'
          : 'brightness(0.6) saturate(0.5)',
    },
    cursor: isActive ? 'default' : 'pointer',
    whileHover: !showLoadingOverlay
      ? { filter: 'brightness(1) saturate(1)' }
      : undefined,
  } satisfies MotionPropsFor<SystemProps>);

export const useUndoRedoAriaLabel = (index: number) => {
  const t = useHomeownerTranslate();
  const { history } = useCustomization();

  if (index === history.activeIndex - 1) return t('customize.undo');
  if (index === history.activeIndex + 1) return t('customize.redo');

  return undefined;
};

export const useEntryImage = (index: number) => {
  const { history } = useCustomization();

  const entry = history.entries[index] as CustomizationHistoryEntry | undefined;
  const { aiGeneratedImageId } = entry ?? {};

  return useAiGeneratedImageQuery({
    variables: { id: String(aiGeneratedImageId) },
    skip: !aiGeneratedImageId,
  });
};
